import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { formatElapsedTime, formatTime } from '@/utils/timeUtils';
import clsx from 'clsx';
import { 
  Users, 
  DollarSign, 
  Trophy, 
  Clock, 
  TrendingUp, 
  Target,
  RotateCcw,
  Plus
} from 'lucide-react';

export const TournamentStats: React.FC = () => {
  const tournament = useSelector((state: RootState) => state.tournament.current);
  const players = useSelector((state: RootState) => state.players.players);
  const { currentRound, secondsRemaining } = useSelector((state: RootState) => state.clock);

  const stats = useMemo(() => {
    if (!tournament) return null;

    const activePlayers = players.filter(p => !p.timeOut);
    const eliminatedPlayers = players.filter(p => p.timeOut);
    const unpaidPlayers = players.filter(p => !p.paid);
    const totalRebuys = players.reduce((sum, p) => sum + p.rebuys, 0);
    const totalAddOns = players.reduce((sum, p) => sum + p.addOns, 0);

    // Calculate pot
    const entryRevenue = players.length * tournament.settings.entryFee;
    const rebuyRevenue = totalRebuys * tournament.settings.rebuyFee;
    const addOnRevenue = totalAddOns * tournament.settings.addOnFee;
    const totalRevenue = entryRevenue + rebuyRevenue + addOnRevenue;
    
    const fixedRake = tournament.settings.fixedRake;
    const perPlayerRake = players.length * tournament.settings.perPlayerRake;
    const rebuyRake = tournament.settings.applyRakeToRebuys ? totalRebuys * tournament.settings.perPlayerRake : 0;
    const addOnRake = tournament.settings.applyRakeToAddOns ? totalAddOns * tournament.settings.perPlayerRake : 0;
    const totalRake = fixedRake + perPlayerRake + rebuyRake + addOnRake;
    
    const pot = totalRevenue - totalRake;

    // Calculate average stack
    const totalChips = activePlayers.reduce((sum, p) => sum + p.chips, 0);
    const averageStack = activePlayers.length > 0 ? Math.floor(totalChips / activePlayers.length) : 0;

    // Calculate time stats
    const elapsedTime = tournament.state.startTime ? 
      formatElapsedTime(tournament.state.startTime) : '00:00:00';

    return {
      totalPlayers: players.length,
      activePlayers: activePlayers.length,
      eliminatedPlayers: eliminatedPlayers.length,
      unpaidPlayers: unpaidPlayers.length,
      totalRebuys,
      totalAddOns,
      pot,
      rake: totalRake,
      averageStack,
      elapsedTime,
      totalChips,
    };
  }, [tournament, players]);

  if (!tournament || !stats) {
    return null;
  }

  const formatCurrency = (amount: number) => {
    return `${tournament.settings.currencySymbol}${amount.toLocaleString()}`;
  };

  const currentRoundData = tournament.rounds[currentRound - 1];
  const nextRoundData = tournament.rounds[currentRound];

  const statCards = [
    {
      title: 'Players',
      value: stats.activePlayers,
      subtitle: `${stats.totalPlayers} total, ${stats.eliminatedPlayers} eliminated`,
      icon: Users,
      color: 'blue',
      trend: stats.eliminatedPlayers > 0 ? 'down' : 'neutral',
    },
    {
      title: 'Prize Pool',
      value: formatCurrency(stats.pot),
      subtitle: `Rake: ${formatCurrency(stats.rake)}`,
      icon: DollarSign,
      color: 'green',
      trend: 'up',
    },
    {
      title: 'Average Stack',
      value: stats.averageStack.toLocaleString(),
      subtitle: `Total chips: ${stats.totalChips.toLocaleString()}`,
      icon: TrendingUp,
      color: 'purple',
      trend: 'neutral',
    },
    {
      title: 'Elapsed Time',
      value: stats.elapsedTime,
      subtitle: `Round ${currentRound}/${tournament.rounds.length}`,
      icon: Clock,
      color: 'orange',
      trend: 'up',
    },
  ];

  const additionalStats = [
    {
      label: 'Rebuys',
      value: stats.totalRebuys,
      icon: RotateCcw,
      color: 'text-blue-600',
    },
    {
      label: 'Add-ons',
      value: stats.totalAddOns,
      icon: Plus,
      color: 'text-purple-600',
    },
    {
      label: 'Unpaid',
      value: stats.unpaidPlayers,
      icon: Target,
      color: 'text-red-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          
          return (
            <div key={stat.title} className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    'p-3 rounded-lg',
                    {
                      'bg-blue-100': stat.color === 'blue',
                      'bg-green-100': stat.color === 'green',
                      'bg-purple-100': stat.color === 'purple',
                      'bg-orange-100': stat.color === 'orange',
                    }
                  )}>
                    <Icon size={24} className={clsx(
                      {
                        'text-blue-600': stat.color === 'blue',
                        'text-green-600': stat.color === 'green',
                        'text-purple-600': stat.color === 'purple',
                        'text-orange-600': stat.color === 'orange',
                      }
                    )} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500">{stat.subtitle}</p>
            </div>
          );
        })}
      </div>

      {/* Current Round Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Round */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target size={20} />
            Current Round
          </h3>
          
          {currentRoundData ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Round:</span>
                <span className="font-semibold">{currentRound}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Time Remaining:</span>
                <span className="font-mono font-semibold text-lg">{formatTime(secondsRemaining)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Small Blind:</span>
                <span className="font-semibold">{formatCurrency(currentRoundData.smallBlind)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Big Blind:</span>
                <span className="font-semibold">{formatCurrency(currentRoundData.bigBlind)}</span>
              </div>
              {currentRoundData.ante > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Ante:</span>
                  <span className="font-semibold">{formatCurrency(currentRoundData.ante)}</span>
                </div>
              )}
              {currentRoundData.breakMinutes > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Break After:</span>
                  <span className="font-semibold">{currentRoundData.breakMinutes}m</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500">No round data available</p>
          )}
        </div>

        {/* Next Round */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy size={20} />
            Next Round
          </h3>
          
          {nextRoundData ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Round:</span>
                <span className="font-semibold">{currentRound + 1}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold">{nextRoundData.minutes}m</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Small Blind:</span>
                <span className="font-semibold">{formatCurrency(nextRoundData.smallBlind)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Big Blind:</span>
                <span className="font-semibold">{formatCurrency(nextRoundData.bigBlind)}</span>
              </div>
              {nextRoundData.ante > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Ante:</span>
                  <span className="font-semibold">{formatCurrency(nextRoundData.ante)}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Trophy size={32} className="mx-auto text-gold-500 mb-2" />
              <p className="text-gray-500">Final Round!</p>
            </div>
          )}
        </div>
      </div>

      {/* Additional Stats */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Statistics</h3>
        <div className="grid grid-cols-3 gap-6">
          {additionalStats.map((stat) => {
            const Icon = stat.icon;
            
            return (
              <div key={stat.label} className="text-center">
                <div className="flex justify-center mb-2">
                  <Icon size={20} className={stat.color} />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};